import React, { useState, createContext, useEffect, useCallback } from 'react';
import { MqttContextType } from '../../types';
import client from '@/lib/feathers';
import getMqttClient from '@/lib/mqtt';

export const MqttContext = createContext<MqttContextType>({
  mqttData: {
    chargingPiles: {},
    groups: {},
  },
});

const MqttContextProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [mqttData, setMqttData] = useState<MqttContextType['mqttData']>({
    chargingPiles: {},
    groups: {},
  });
  const [community, setCommunity] = useState<{
    name: string;
    englishName: string;
  } | null>(null);

  const getCommunity = useCallback(async () => {
    try {
      const res = await client.service('communityData').find({});
      setCommunity(res.data[0]);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getCommunity();
  }, [getCommunity]);

  useEffect(() => {
    if (community) {
      const mqttClient = getMqttClient();
      const path = `EZchargingPile/status/${community.englishName}/#`;
      mqttClient.subscribe(path);
      mqttClient.on('message', (topic, message) => {
        const path = topic.split('/');
        if (path.length === 6) {
          const [type, name, dataKey] = topic.split('/').slice(-3);
          setMqttData((prev) => {
            if (prev) {
              const newData = { ...prev };
              if (!newData[type]) {
                newData[type] = {};
              }
              if (!newData[type][name]) {
                newData[type][name] = {};
              }
              const rawData = message.toString();
              if (!rawData) {
                return prev;
              }
              let data: unknown;
              try {
                data = JSON.parse(rawData);
                if (typeof data === 'number' && dataKey !== 'waitingOrder') {
                  data = Number(rawData).toFixed(2);
                }
              } catch {
                data = rawData;
              }
              newData[type][name][dataKey] = data;
              return newData;
            }
            return prev;
          });
        }
      });
      return () => {
        mqttClient.end();
      };
    }
    return () => {
      return;
    };
  }, [community]);

  return (
    <MqttContext.Provider value={{ mqttData }}>{children}</MqttContext.Provider>
  );
};

export default MqttContextProvider;
